/* v88-rail.js — the V88 card rails.
 *
 * SWIPER DRIVES THESE, not scroll-snap. They began as native overflow +
 * scroll-snap with a hand-written drift on top, which meant re-implementing
 * momentum, wrapping, autoplay and drag — and doing it worse: the wrap was a
 * 300ms snap back to zero, snap fought every programmatic scroll, and on a
 * phone `scroll-behavior: smooth` was not even declared. Swiper is already on
 * the page for the PDP gallery, so the rails use it.
 *
 * The markup stays as the sections render it — a rail with slide children.
 * Swiper's structure (.swiper > .swiper-wrapper > .swiper-slide) is built
 * here at run time, so no section had to be rewritten and the CSS that sizes
 * the cards still applies.
 *
 * Without Swiper the rail is still an overflow container that scrolls, swipes
 * and takes the keyboard — that is the fallback, and it is why the CSS keeps
 * `overflow-x: auto` rather than relying on the library.
 *
 *   data-v88-rail       mark the element
 *   data-v88-static     leave it alone: no Swiper, no wrapper. For a row
 *                       with too few cards to scroll, which then keeps
 *                       whatever layout the stylesheet gives it.
 *   data-v88-autoplay   ms between slides. Absent means no autoplay on a
 *                       Swiper rail; the cross-fading pager falls back to 8s.
 *   data-v88-loop       "false" to stop it wrapping
 */
(function () {
  'use strict';

  /* AUTOPLAY ONLY WHILE IT CAN BE SEEN (2026-10-14, Emily: "make sure the
     before/after carousel hasn't started scrolling to the end, before user
     has even scrolled to that section"). Autoplay began at page load, so a
     rail that does not loop (the stories, data-v88-loop="false") had walked
     to its last card and stopped long before anyone reached it. It now
     turns while at least a third of it is on screen and holds when it
     leaves. Without IntersectionObserver it simply plays, as before. */
  function whileInView(el, on, off) {
    if (typeof IntersectionObserver !== 'function') { on(); return; }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) on(); else off(); });
    }, { threshold: 0.33 }).observe(el);
  }

  function bind(rail) {
    /* Marked by the section as having nothing to scroll. Binding anyway
       would wrap the cards in a transformed .swiper-wrapper and put them
       beyond the reach of the stylesheet that lays the row out. */
    if (rail.hasAttribute('data-v88-static')) return;

    /* THE RAIL'S OWN GROUP FIRST, the section only as a fallback. The
       ingredients section carries one group per tab inside a single
       <section>, so a section-wide search collected all three tabs' dots
       and every rail drove every indicator. Sections with a single rail
       have no [data-v88-group] and are unaffected. */
    var root = rail.closest('[data-v88-group]') || rail.closest('section') || rail.parentNode;
    var dots = [].slice.call(root.querySelectorAll('[data-v88-dot]'));
    var slides = [].slice.call(rail.querySelectorAll('[data-v88-slide]'));
    if (!slides.length) return;

    /* MEASURED FIRST, before a single class is added. swiper-bundle.min.css
       sizes .swiper-slide at 100% of the rail, so the moment the cards are
       wrapped every slide measures a full rail wide and any "do they fit"
       question answers itself wrongly — which is how the experts rail ended
       up looping a row that was already whole. */
    var gapNow = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
    var content = slides.reduce(function (sum, el) {
      return sum + el.getBoundingClientRect().width;
    }, 0) + gapNow * Math.max(0, slides.length - 1);
    var fits = content <= rail.clientWidth + 1;
    /* A row that is already whole has nothing to page through, so its dots
       come down (Sep 15). Inline, because the dots' own rule is display:
       flex and would beat the hidden attribute. */
    if (fits) {
      var dotsRow = root.querySelector('[data-v88-dots], .v88-clin__dots');
      if (dotsRow) dotsRow.style.display = 'none';
    }

    /* Build Swiper's DOM around what the section rendered. */
    var wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    while (rail.firstChild) wrapper.appendChild(rail.firstChild);
    rail.appendChild(wrapper);
    rail.classList.add('swiper', 'v88-rail--swiper');

    /* Each card gets its own slide box rather than becoming the slide.
       swiper-bundle.min.css loads in the body — after v88.css — so its
       `.swiper-slide { width: 100% }` outranks every card's own width and
       the rail collapses to one card per view. The wrapper takes that rule
       instead, is set back to auto here, and the card inside keeps the width
       its section gave it. */
    slides = slides.map(function (card) {
      var slide = document.createElement('div');
      slide.className = 'swiper-slide';
      card.parentNode.insertBefore(slide, card);
      slide.appendChild(card);
      return slide;
    });

    /* The gap is CSS's, so Swiper is told what it already is rather than
       being given a second source of truth. */
    var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
    var every = parseInt(rail.getAttribute('data-v88-autoplay'), 10);
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* `fits` was worked out at the top, on the cards themselves. */
    var opts = {
      slidesPerView: 'auto',
      spaceBetween: gap,
      grabCursor: !fits,
      watchOverflow: true,
      a11y: { enabled: true },
      keyboard: { enabled: true, onlyInViewport: true }
    };
    /* No loop and no autoplay when the row is already whole: looping a row
       that fits is how it ends up half a card out of line. */
    if (!fits && rail.getAttribute('data-v88-loop') !== 'false') opts.loop = true;
    if (!fits && every > 0 && !still) {
      /* Without a loop the rail stops at its last card rather than jumping
         back to the first, which is the other half of "no unlimited loop". */
      opts.autoplay = { delay: every, disableOnInteraction: false, pauseOnMouseEnter: true, stopOnLastSlide: !opts.loop };
    }
    /* data-v88-drift="5000": a slow, continuous drift (5s per card, linear)
       rather than a slide every few seconds — the video wall's ask (Sep 15).
       It needs the loop, and the wrapper's easing goes linear in v88.css. */
    var drift = parseInt(rail.getAttribute('data-v88-drift'), 10);
    if (!fits && drift > 0 && !still) {
      opts.loop = true;
      opts.speed = drift;
      opts.autoplay = { delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true };
      rail.classList.add('v88-rail--drift');
    }

    var sw = new Swiper(rail, opts);
    if (opts.autoplay && sw.autoplay) {
      sw.autoplay.stop();
      whileInView(rail, function () { sw.autoplay.start(); }, function () { sw.autoplay.stop(); });
    }

    /* Arrows, where a section draws them (the guarantee band does). Same
       reasoning as the dots: the section owns the markup and Swiper is
       told what to do, rather than being asked to render its own. */
    var prev = root.querySelector('[data-v88-prev]');
    var next = root.querySelector('[data-v88-next]');
    if (prev) prev.addEventListener('click', function () { sw.slidePrev(); });
    if (next) next.addEventListener('click', function () { sw.slideNext(); });

    /* The dots are the section's own markup, so they are driven rather than
       replaced — Swiper's bullets would need their own styling and would not
       match the board. realIndex, not activeIndex: with loop on, Swiper adds
       duplicate slides and activeIndex counts them. */
    if (dots.length) {
      var paint = function () {
        dots.forEach(function (d, i) {
          d.classList.toggle('is-on', i === sw.realIndex);
          d.setAttribute('aria-selected', i === sw.realIndex ? 'true' : 'false');
        });
      };
      sw.on('slideChange', paint);
      dots.forEach(function (d, i) {
        d.addEventListener('click', function () {
          if (sw.params.loop) sw.slideToLoop(i); else sw.slideTo(i);
        });
      });
      paint();
    }
  }

  /* A rail behind a closed toggle has no width, and a row measured at zero
     never fits — the hidden half of the ingredients section would come out
     looping while the half you can see sits still. So a rail that is not
     laid out yet is left alone until it is, and measured then. */

  /* TWO CARDS, CROSS-FADING — the home board's "Clinical Ingredients, Real
   * Results" on desktop.
   *
   * Not a slider: the pair does not move sideways, it dissolves into the
   * next pair. Swiper's own fade effect only works one slide at a time, so
   * the cards are laid out as a grid instead — every card in row one,
   * column (index mod two) — which stacks page two exactly on top of page
   * one and makes the box as tall as the tallest card in the whole set, so
   * nothing jumps as the pages change.
   *
   * It turns on the section's own interval (data-v88-autoplay, 8s if the
   * section sets none) and holds only while the keyboard is inside it.
   *
   * Only the home page's rail takes this. The product pages carry the same
   * section with different copy and keep their slider.
   */
  function pager(rail, per) {
    var root = rail.closest('section') || rail.parentNode;
    var cards = [].slice.call(rail.querySelectorAll('[data-v88-slide]'));
    if (cards.length <= per) return false;
    var pages = Math.ceil(cards.length / per);

    rail.classList.add('v88-rail--pager');
    rail.style.setProperty('--v88-pager-cols', per);
    cards.forEach(function (c, i) {
      c.style.gridColumn = String((i % per) + 1);
      /* AOS owns opacity too, and two things fading one card is one too
         many — the scroll animation would strand a hidden page visible. */
      c.removeAttribute('data-aos');
      c.removeAttribute('data-aos-delay');
      c.classList.remove('aos-init', 'aos-animate');
    });

    var dots = [].slice.call(root.querySelectorAll('[data-v88-dot]'));
    dots.forEach(function (d, i) { if (i >= pages) d.style.display = 'none'; });

    var at = 0, timer = null;
    function show(p) {
      at = (p + pages) % pages;
      cards.forEach(function (c, i) {
        var on = Math.floor(i / per) === at;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach(function (d, i) {
        if (i >= pages) return;
        d.classList.toggle('is-on', i === at);
        d.setAttribute('aria-selected', i === at ? 'true' : 'false');
      });
    }

    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* The section sets this (seconds in the editor, ms here). 8s is the
       fallback for a rail that does not: four read as too fast to finish
       reading a card before it turned (Sep 23). */
    var every = parseInt(rail.getAttribute('data-v88-autoplay'), 10) || 8000;
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() { stop(); if (!still) timer = setInterval(function () { show(at + 1); }, every); }

    dots.forEach(function (d, i) {
      if (i >= pages) return;
      d.addEventListener('click', function () { show(i); start(); });
    });
    /* It keeps turning unless someone is actually in it — focus, not
       hover. Pausing under the pointer sounds polite and reads as broken:
       a pointer resting anywhere over a band this wide is the normal way
       to watch it, and the pair would simply stop changing. */
    var seen = false;
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', function () { if (seen) start(); });

    show(0);
    whileInView(rail, function () { seen = true; start(); }, function () { seen = false; stop(); });
    return true;
  }

  /* The stories rail pages wherever it appears (Akaash, Sep 23 — "please
     make carousel like homepage"). It was the home page alone, keyed on
     .v88-hero; the PDP draws the same section with its own copy and was
     left on the slider, so the two boards behaved differently for no
     reason a reader could see. Both now, at the same breakpoint. */
  function wantsPager(rail) {
    return rail.classList.contains('v88-tst__rail')
      && !!document.querySelector('.v88-hero, .v88-pdp')
      && window.matchMedia('(min-width: 900px)').matches;
  }

  /* A CARD is what has to be measurable, not the rail. The rail inside a
     hidden tab still reports a width — only its `display: none` subtree
     measures zero — so waiting on rail.clientWidth let the ingredients'
     hidden groups bind with every card at 0. `fits` then came out false
     for a row of two cards that plainly fits, and the row was handed to
     Swiper: dots under it, and the cards locked in a transformed
     .swiper-wrapper that no justify-content on the rail can centre
     (Akaash, Sep 23 — "centre the ingredients"). */
  function laidOut(rail) {
    var card = rail.querySelector('[data-v88-slide]');
    return !!card && card.getBoundingClientRect().width > 0;
  }

  /* A rail in a closed tab is bound when its tab opens, not at load.
     Measuring one that is not on screen is how a two-card row came out as
     "does not fit" and was handed to Swiper. The switch hides its groups
     after this script runs, so a rail can also look laid out here and be
     hidden a moment later; deferring to the first real layout is what
     makes the measurement trustworthy either way. */
  function bindWhenSized(rail) {
    if (laidOut(rail) || typeof ResizeObserver !== 'function') return bind(rail);
    var ro = new ResizeObserver(function () {
      if (laidOut(rail)) { ro.disconnect(); bind(rail); }
    });
    /* The cards are what changes size when the tab opens, so the observer
       watches one of those rather than the rail, which never changes. */
    ro.observe(rail.querySelector('[data-v88-slide]') || rail);
  }

  function boot() {
    var rails = document.querySelectorAll('[data-v88-rail]');
    if (!rails.length) return;

    /* AFTER THE SWITCH HAS HIDDEN ITS TABS. Both scripts are deferred and
       this one is first, so at this point every group of a switchable
       section is still laid out — the closed tabs included. Measuring then
       reads a layout that is about to change. One frame is enough for
       v88-switch.js to have run and the stylesheet to have settled. */
    var switchable = document.querySelector('[data-v88-switchable]');
    if (switchable && !boot.deferred) {
      boot.deferred = true;
      return requestAnimationFrame(function () { requestAnimationFrame(boot); });
    }

    // Swiper is loaded from theme.liquid with defer, so on a cold cache it can
    // still be pending. Poll briefly rather than racing it; if it never
    // arrives the rails stay plain scrollers.
    var tries = 0;
    (function attempt() {
      if (typeof window.Swiper === 'function') {
        Array.prototype.forEach.call(rails, function (r) {
          if (r.classList.contains('v88-rail--swiper') || r.classList.contains('v88-rail--pager')) return;
          if (wantsPager(r) && pager(r, 2)) return;
          bindWhenSized(r);
        });
        return;
      }
      if (++tries < 40) return setTimeout(attempt, 100);

      /* Swiper never arrived. The rails are still overflow scrollers, so
         any arrows a section drew are wired to that instead of left dead. */
      Array.prototype.forEach.call(rails, function (rail) {
        var root = rail.closest('section') || rail.parentNode;
        var step = function (dir) {
          var slide = rail.querySelector('[data-v88-slide]');
          var by = slide ? slide.getBoundingClientRect().width + 16 : rail.clientWidth;
          rail.scrollBy({ left: dir * by, behavior: 'smooth' });
        };
        var p = root.querySelector('[data-v88-prev]');
        var n = root.querySelector('[data-v88-next]');
        if (p) p.addEventListener('click', function () { step(-1); });
        if (n) n.addEventListener('click', function () { step(1); });
      });
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
